import { Inject, Injectable } from '@angular/core';
import {
    ConfigConstants,
    Equipment,
    initialAddressState,
    initialNotificationStatusState,
    isDefined,
    Notification,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifier,
    NotificationIdentifierModel,
    NotificationInterface,
    NotificationModel,
    NotificationStatus,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EquipmentModel } from '../../interfaces/equipment.interface';
import { OdataEquipmentsModel } from '../../interfaces/equipments-odata.interface';

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements NotificationInterface {
    private notification: Notification;
    public constructor(@Inject(LogInstanceToken) private readonly log: LogInterface) {}

    public getMainEquipmentFromNotification(notification: Notification): Equipment | undefined {
        // First equipment from the array of equipment set is always be the main equipment
        return notification.equipmentSet[0];
    }

    public getNotificationByIdentifier(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        notificationIdentifier: NotificationIdentifierModel,
    ): NotificationModel | undefined {
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getNotificationStateByEquipment(equipmentId: string): Notification[] {
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getNotificationStateByIdentifier(notificationIdentifier: NotificationIdentifier): Notification | undefined {
        if (!isDefined(this.notification)) {
            throw new Error(
                `NotificationService: getNotificationStateByIdentifier: Calling before dummy notification has been created`,
            );
        }
        return this.notification;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getStatusStateByIdentifier(notificationIdentifier: NotificationIdentifier): NotificationStatus {
        if (!isDefined(this.notification)) {
            throw new Error(
                `NotificationService: getStatusStateByIdentifier: Calling before dummy notification has been created`,
            );
        }
        return this.notification.updatedStatus;
    }

    public createDummyNotificationForFI(protocol: OdataEquipmentsModel): void {
        // Creating dummy notification as there is no role of notifications in FI
        this.notification = {
            identifier: {
                frontId: protocol.User,
                qmNum: protocol.EquipmentsSet.results[0].OrderNumber,
                // Hardcode maNum to 0001 as this is a dummy notification and nothing is related to Gateway
                maNum: '0001',
            },
            backendStatus: {
                ...initialNotificationStatusState,
            },
            // updated status should always be OSR otherwise forms won't be opened in editable mode
            updatedStatus: {
                ...initialNotificationStatusState,
                statusCode: NOTIFICATION_STATUS_CODE.OSR,
            },
            languageId: '',
            notificationType: ConfigConstants.NotificationTypes.Installation,
            address: {
                ...initialAddressState,
                name1: protocol.EquipmentsSet.results[0].Name1
                    ? protocol.EquipmentsSet.results[0].Name1
                    : protocol.EquipmentsSet.results[0].StName1,
                name2: protocol.EquipmentsSet.results[0].Name2
                    ? protocol.EquipmentsSet.results[0].Name2
                    : protocol.EquipmentsSet.results[0].StName2,
                street: protocol.EquipmentsSet.results[0].Street
                    ? protocol.EquipmentsSet.results[0].Street
                    : protocol.EquipmentsSet.results[0].StStreet,
                city: protocol.EquipmentsSet.results[0].Address
                    ? protocol.EquipmentsSet.results[0].Address
                    : protocol.EquipmentsSet.results[0].StAddress,
                department: protocol.EquipmentsSet.results[0].Department,
                country: protocol.EquipmentsSet.results[0].Country
                    ? protocol.EquipmentsSet.results[0].Country
                    : protocol.EquipmentsSet.results[0].HcCountry,
            },
            equipmentSet: [
                {
                    equnr: protocol.EquipmentsSet.results[0].EquipmentHq,
                    tidnr: '',
                    matnr: protocol.EquipmentsSet.results[0].PartNo,
                    sernr: protocol.EquipmentsSet.results[0].SerialNo,
                    equiShortDesc: protocol.EquipmentsSet.results[0].Description,
                    division: '',
                    mainEquipmentIndicator: '',
                },
            ],
            emailSet: [],
            functionalLocation: protocol.EquipmentsSet.results[0].Location,
        };
    }

    public setEquipmentForNotification(equipment: EquipmentModel): void {
        this.log.debug(
            `NotificationService(FI): setEquipmentForNotification: data received as params ${JSON.stringify(equipment)}`,
        );
        this.notification = {
            ...this.notification,
            identifier: {
                ...this.notification.identifier,
                qmNum: equipment.OrderNumber,
            },
            equipmentSet: [
                {
                    equnr: equipment.EquipmentHq,
                    tidnr: '',
                    matnr: equipment.PartNo,
                    sernr: equipment.SerialNo,
                    equiShortDesc: equipment.Description,
                    division: '',
                    mainEquipmentIndicator: '',
                },
            ],
            functionalLocation: equipment.Location,
        };
    }

    public getAllSPQDocIDs(): string[] {
        this.log.warn('NotificationService: getAllSPQDocIDs: not implemented yet');
        return [];
    }

    public getNotificationsInOSROrHigher(): Notification[] {
        this.log.warn('NotificationService: getNotificationsInOSROrHigher: not implemented yet');
        return [];
    }
}
